import { Icon, TIconName } from '@apia/icons';
import { Box } from '@apia/theme';
import { EventEmitter, useMount } from '@apia/util';
import {
  ControlledMenu,
  MenuItem,
  MenuItemProps,
  ControlledMenuProps,
  MenuDivider,
  SubMenu,
} from '@szhsin/react-menu';
import { ReactNode, useCallback, useState } from 'react';
import { FreeFocusInside } from 'react-focus-lock';

export type TSubmenu = { label: string; items: TMenuItems };

export type TMenuItem =
  | (MenuItemProps & { icon?: TIconName; key: string })
  | 'separator'
  | TSubmenu;

export type TMenuItems = TMenuItem[];

function isSubmenu(item: TMenuItem): item is TSubmenu {
  return Array.isArray((item as TSubmenu).items);
}

export class ApiaUtilMenu {
  #emitter = new EventEmitter<{ toggle: boolean }>();
  #items: TMenuItems = [];
  menuProps: ControlledMenuProps | null = null;

  #hooks = {
    useIsOpen: (): [boolean, () => void] => {
      const [isOpen, setIsOpen] = useState(false);

      useMount(() => {
        return this.#emitter.on('toggle', setIsOpen);
      });

      return [
        isOpen,
        useCallback(() => {
          setIsOpen(false);
        }, []),
      ];
    },
  };

  close = () => {
    this.#emitter.emit('toggle', false);
  };

  open = ({
    menuProps,
    items,
  }: {
    menuProps: ControlledMenuProps;
    items: TMenuItems;
  }) => {
    this.#items = items;
    this.menuProps = menuProps;

    this.#emitter.emit('toggle', true);
  };

  #maxId = 0;

  #RenderMenu = (submenu: TSubmenu) => {
    return (
      <>
        {submenu.items.map((current, i) => {
          if (current === 'separator')
            return <MenuDivider key={`separator${i}`} />;

          if (isSubmenu(current)) {
            return (
              <SubMenu label={current.label} key={`submenu${this.#maxId++}`}>
                {this.#RenderMenu(current)}
              </SubMenu>
            );
          }

          const children = current.icon ? (
            <Box className="withIcon">
              <Icon name={current.icon} title={current.title!} />{' '}
              {current.children as ReactNode}
            </Box>
          ) : (
            current.children
          );

          return (
            <MenuItem {...current} key={current.key}>
              {children}
            </MenuItem>
          );
        })}
      </>
    );
  };

  Component = () => {
    const [isOpen, close] = this.#hooks.useIsOpen();

    if (!isOpen) return null;

    return (
      <FreeFocusInside>
        <ControlledMenu
          {...this.menuProps}
          state={isOpen ? 'open' : 'closed'}
          onClose={(ev) => {
            close();
            this.menuProps?.onClose?.(ev);
          }}
        >
          {this.#RenderMenu({ label: '', items: this.#items })}
        </ControlledMenu>
      </FreeFocusInside>
    );
  };
}
